import { useState, useEffect, useRef } from 'react'
import { io } from 'socket.io-client'
import {
  MessageSquare,
  Send,
  Plus,
  Users,
  Hash,
  Trash2,
  Loader2,
  AlertCircle,
  Settings,
  UserPlus,
  Lock,
  Globe,
  Ban,
  MoreVertical,
  Eraser
} from 'lucide-react'
import { chatAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { can } from '@/lib/permissions'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

export default function ChatPage() {
  const [rooms, setRooms] = useState([])
  const [activeRoom, setActiveRoom] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [socket, setSocket] = useState(null)
  const { user: currentUser } = useAuth()
  const [roomMembers, setRoomMembers] = useState([])
  const [typingUsers, setTypingUsers] = useState([])

  // Dialog states
  const [createRoomDialogOpen, setCreateRoomDialogOpen] = useState(false)
  const [roomFormData, setRoomFormData] = useState({
    name: '',
    description: '',
    isPublic: true,
  })
  const [muteDialogOpen, setMuteDialogOpen] = useState(false)
  const [muteFormData, setMuteFormData] = useState({
    userId: null,
    username: '',
    durationMinutes: 30,
    reason: '',
    isPermanent: false,
    roomSpecific: true,
  })

  const messagesEndRef = useRef(null)
  const typingTimeoutRef = useRef(null)
  const activeRoomRef = useRef(activeRoom)
  const currentUserRef = useRef(currentUser)

  // Keep refs in sync with state
  useEffect(() => {
    activeRoomRef.current = activeRoom
  }, [activeRoom])

  useEffect(() => {
    currentUserRef.current = currentUser
  }, [currentUser])

  // Initialize Socket.IO connection (only once)
  useEffect(() => {
    console.log('Initializing Socket.IO connection...');
    const token = localStorage.getItem('auth_token');
    console.log('Auth token:', token ? 'Present' : 'Missing');

    // Use relative path to leverage Vite's proxy configuration
    const newSocket = io({
      auth: {
        token: token
      },
      withCredentials: true,
      transports: ['websocket', 'polling']
    })

    setSocket(newSocket)

    newSocket.on('connect', () => {
      console.log('Connected to chat server, socket ID:', newSocket.id)
    })

    newSocket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error.message)
      console.error('Error details:', error)
    })

    newSocket.on('error', (error) => {
      console.error('Socket.IO error:', error)
    })

    newSocket.on('new-chat-message', (data) => {
      console.log('Received new-chat-message:', data)
      if (data.chatMessage) {
        // Use ref to get current activeRoom without closure issues
        const currentActiveRoom = activeRoomRef.current
        if (currentActiveRoom && data.chatMessage.room_id === currentActiveRoom.id) {
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === data.chatMessage.id)) {
              return prev
            }
            return [...prev, data.chatMessage]
          })
        }
      }
    })

    newSocket.on('user-typing', (data) => {
      // Use refs to get current values without closure issues
      const currentActiveRoom = activeRoomRef.current
      const currentUser = currentUserRef.current

      if (currentActiveRoom && data.roomId === currentActiveRoom.id && data.username !== currentUser?.username) {
        setTypingUsers(prev => {
          const filtered = prev.filter(u => u !== data.username)
          return data.isTyping ? [...filtered, data.username] : filtered
        })
      }
    })

    newSocket.on('user-joined-room', (data) => {
      console.log('User joined room:', data)
      const currentActiveRoom = activeRoomRef.current

      // Only update if this is the active room
      if (currentActiveRoom && data.roomId === currentActiveRoom.id) {
        setRoomMembers(prev => {
          // Avoid duplicates
          if (prev.some(m => m.username === data.username)) {
            return prev
          }
          // Add new member
          return [...prev, {
            username: data.username,
            display_name: data.displayName,
            socketId: data.socketId
          }]
        })
      }
    })

    newSocket.on('user-left-room', (data) => {
      console.log('User left room:', data)
      const currentActiveRoom = activeRoomRef.current

      // Only update if this is the active room
      if (currentActiveRoom && data.roomId === currentActiveRoom.id) {
        setRoomMembers(prev =>
          prev.filter(m => m.username !== data.username)
        )
      }
    })

    return () => newSocket.close()
  }, [])

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms()
  }, [])

  // Join room when active room changes
  useEffect(() => {
    if (socket && activeRoom) {
      socket.emit('join-chat-room', activeRoom.id)
      fetchMessages(activeRoom.id)
      fetchRoomMembers(activeRoom.id)

      // Clear typing indicators when switching rooms
      setTypingUsers([])

      return () => {
        socket.emit('leave-chat-room', activeRoom.id)
      }
    }
  }, [socket, activeRoom])

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchRooms = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await chatAPI.getRooms()
      setRooms(response.data.rooms || [])

      // Auto-select first room if none selected
      if (!activeRoom && response.data.rooms.length > 0) {
        setActiveRoom(response.data.rooms[0])
      }
    } catch (err) {
      console.error('Failed to fetch rooms:', err)
      setError('Failed to load chat rooms')
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async (roomId) => {
    try {
      const response = await chatAPI.getMessages(roomId, { limit: 100 })
      setMessages(response.data.messages || [])
    } catch (err) {
      console.error('Failed to fetch messages:', err)
    }
  }

  const fetchRoomMembers = async (roomId) => {
    try {
      const response = await chatAPI.getRoomMembers(roomId)
      setRoomMembers(response.data.members || [])
    } catch (err) {
      console.error('Failed to fetch room members:', err)
    }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()

    if (!newMessage.trim() || !activeRoom) return

    try {
      const response = await chatAPI.sendMessage(activeRoom.id, newMessage.trim())

      // Emit via socket for real-time update
      if (socket) {
        socket.emit('chat-message', {
          roomId: activeRoom.id,
          chatMessage: response.data.chatMessage
        })
      }

      setNewMessage('')

      // Stop typing indicator
      if (socket) {
        socket.emit('typing', {
          roomId: activeRoom.id,
          username: currentUser?.username,
          isTyping: false
        })
      }
    } catch (err) {
      console.error('Failed to send message:', err)
      alert('Failed to send message')
    }
  }

  const handleTyping = (e) => {
    setNewMessage(e.target.value)

    if (!socket || !activeRoom) return

    // Emit typing event
    socket.emit('typing', {
      roomId: activeRoom.id,
      username: currentUser?.username || currentUser?.display_name,
      isTyping: true
    })

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('typing', {
        roomId: activeRoom.id,
        username: currentUser?.username,
        isTyping: false
      })
    }, 2000)
  }

  const handleCreateRoom = async () => {
    try {
      if (!roomFormData.name.trim()) {
        alert('Room name is required')
        return
      }

      await chatAPI.createRoom(roomFormData)
      setCreateRoomDialogOpen(false)
      setRoomFormData({ name: '', description: '', isPublic: true })
      fetchRooms()
    } catch (err) {
      console.error('Failed to create room:', err)
      alert(err.response?.data?.error || 'Failed to create room')
    }
  }

  const handleDeleteRoom = async (roomId) => {
    if (!confirm('Are you sure you want to delete this room?')) return

    try {
      await chatAPI.deleteRoom(roomId)
      if (activeRoom?.id === roomId) {
        setActiveRoom(null)
        setMessages([])
      }
      fetchRooms()
    } catch (err) {
      console.error('Failed to delete room:', err)
      alert(err.response?.data?.error || 'Failed to delete room')
    }
  }

  const handleDeleteMessage = async (messageId) => {
    if (!confirm('Delete this message?')) return

    try {
      await chatAPI.deleteMessage(messageId)
      setMessages(prev => prev.filter(m => m.id !== messageId))
    } catch (err) {
      console.error('Failed to delete message:', err)
      alert('Failed to delete message')
    }
  }

  const handleClearRoomMessages = async () => {
    if (!activeRoom) return
    if (!confirm(`Are you sure you want to clear ALL messages in #${activeRoom.name}? This cannot be undone!`)) return

    try {
      await chatAPI.clearRoomMessages(activeRoom.id)
      setMessages([])
      alert('All messages cleared successfully')
    } catch (err) {
      console.error('Failed to clear messages:', err)
      alert(err.response?.data?.error || 'Failed to clear messages')
    }
  }

  const handleOpenMuteDialog = (member) => {
    setMuteFormData({
      userId: member.id,
      username: member.display_name || member.username,
      durationMinutes: 30,
      reason: '',
      isPermanent: false,
      roomSpecific: true,
    })
    setMuteDialogOpen(true)
  }

  const handleMuteUser = async () => {
    try {
      await chatAPI.muteUser(muteFormData.userId, {
        roomId: muteFormData.roomSpecific ? activeRoom?.id : null,
        durationMinutes: muteFormData.isPermanent ? null : muteFormData.durationMinutes,
        reason: muteFormData.reason || null,
        isPermanent: muteFormData.isPermanent,
      })

      setMuteDialogOpen(false)
      setMuteFormData({
        userId: null,
        username: '',
        durationMinutes: 30,
        reason: '',
        isPermanent: false,
        roomSpecific: true,
      })

      alert(`User ${muteFormData.username} has been muted successfully`)
    } catch (err) {
      console.error('Failed to mute user:', err)
      alert(err.response?.data?.error || 'Failed to mute user')
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const getUserInitials = (username, displayName) => {
    const name = displayName || username || '?'
    return name.substring(0, 2).toUpperCase()
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin': return 'text-yellow-400'
      case 'admin': return 'text-red-400'
      case 'referee': return 'text-neon-blue'
      default: return 'text-foreground'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-neon-green mx-auto mb-4" />
          <p className="text-muted-foreground">Loading chat...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Chat</h1>
          <p className="text-muted-foreground mt-1">
            Praat live met de rest van de LAN
          </p>
        </div>
        {can(currentUser?.role, 'manage_chat') && (
          <Button
            onClick={() => setCreateRoomDialogOpen(true)}
            className="flex items-center space-x-2 bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            <span>Nieuwe kamer</span>
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Rooms Sidebar */}
        <div className="col-span-12 md:col-span-3">
          <Card className="border-neon-green/30">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Hash className="h-5 w-5 mr-2 text-neon-green" />
                Kamers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-2">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      onClick={() => setActiveRoom(room)}
                      className={cn(
                        'w-full text-left p-3 rounded-md border transition-all',
                        activeRoom?.id === room.id
                          ? 'bg-neon-green/10 border-neon-green/50 shadow-neon-green'
                          : 'border-border hover:border-neon-green/30 hover:bg-accent'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            {room.is_public ? (
                              <Globe className="h-3 w-3 text-neon-green" />
                            ) : (
                              <Lock className="h-3 w-3 text-neon-purple" />
                            )}
                            <span className="font-medium text-sm truncate">{room.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {room.member_count} leden
                          </p>
                        </div>
                        {can(currentUser?.role, 'manage_chat') && room.name !== 'General' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteRoom(room.id)
                            }}
                            className="text-red-500 hover:text-red-600 h-6 w-6 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Chat Area */}
        <div className="col-span-12 md:col-span-6">
          <Card className="border-neon-green/30 h-[600px] flex flex-col">
            {activeRoom ? (
              <>
                {/* Room Header */}
                <CardHeader className="border-b border-neon">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center text-xl">
                        <MessageSquare className="h-5 w-5 mr-2 text-neon-green" />
                        {activeRoom.name}
                      </CardTitle>
                      {activeRoom.description && (
                        <CardDescription className="text-sm mt-1">
                          {activeRoom.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {can(currentUser?.role, 'manage_chat') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleClearRoomMessages}
                          className="text-red-500 hover:text-red-600 border-red-500/30 hover:border-red-500/50"
                        >
                          <Eraser className="h-4 w-4 mr-2" />
                          Berichten wissen
                        </Button>
                      )}
                      <Badge variant="outline" className="text-neon-green border-neon-green/30">
                        {roomMembers.length} online
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div key={msg.id} className="flex items-start space-x-3 group">
                          <Avatar className="h-8 w-8 border border-neon-green/30">
                            {msg.avatar_url && <AvatarImage src={msg.avatar_url} alt={msg.display_name || msg.username} />}
                            <AvatarFallback className="bg-neon-green/10 text-neon-green text-xs">
                              {getUserInitials(msg.username, msg.display_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className={cn('font-medium text-sm', getRoleColor(msg.role))}>
                                {msg.display_name || msg.username}
                              </span>
                              {msg.role === 'superadmin' && (
                                <Badge variant="outline" className="text-xs px-1 py-0 h-4 text-yellow-400 border-yellow-400/40 bg-yellow-400/10">
                                  Superadmin
                                </Badge>
                              )}
                              {msg.role === 'admin' && (
                                <Badge variant="outline" className="text-xs px-1 py-0 h-4 text-red-400 border-red-400/30">
                                  Admin
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatTime(msg.created_at)}
                              </span>
                              {(can(currentUser?.role, 'manage_chat') || currentUser?.id === msg.user_id) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                            <p className="text-sm mt-1 break-words">{msg.message}</p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>

                {/* Typing Indicator */}
                {typingUsers.length > 0 && (
                  <div className="px-4 py-2 text-xs text-muted-foreground italic">
                    {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                  </div>
                )}

                {/* Message Input */}
                <div className="border-t border-neon p-4">
                  <form onSubmit={handleSendMessage} className="flex space-x-2">
                    <Input
                      value={newMessage}
                      onChange={handleTyping}
                      placeholder="Typ een bericht…"
                      className="flex-1 border-neon-green/30 focus:border-neon-green"
                      maxLength={2000}
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
                      variant="outline"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a room to start chatting</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Members Sidebar */}
        <div className="col-span-12 md:col-span-3">
          <Card className="border-neon-green/30">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Users className="h-5 w-5 mr-2 text-neon-green" />
                Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeRoom ? (
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-2">
                    {roomMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center space-x-3 p-2 rounded-md border border-border hover:bg-accent transition-all group"
                      >
                        <Avatar className="h-8 w-8 border border-neon-green/30">
                          {member.avatar_url && <AvatarImage src={member.avatar_url} alt={member.display_name || member.username} />}
                          <AvatarFallback className="bg-neon-green/10 text-neon-green text-xs">
                            {getUserInitials(member.username, member.display_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', getRoleColor(member.role))}>
                            {member.display_name || member.username}
                          </p>
                          {member.role !== 'user' && (
                            <Badge variant="outline" className="text-xs mt-0.5 h-4 px-1">
                              {member.role}
                            </Badge>
                          )}
                        </div>
                        {can(currentUser?.role, 'manage_chat') && !can(member.role, 'manage_chat') && member.id !== currentUser.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenMuteDialog(member)}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-orange-500 hover:text-orange-600"
                          >
                            <Ban className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No room selected</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Room Dialog */}
      <Dialog open={createRoomDialogOpen} onOpenChange={setCreateRoomDialogOpen}>
        <DialogContent className="border-neon-green/30">
          <DialogHeader>
            <DialogTitle>Create New Chat Room</DialogTitle>
            <DialogDescription>
              Create a new room for your team or event
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="room-name">Room Name</Label>
              <Input
                id="room-name"
                value={roomFormData.name}
                onChange={(e) => setRoomFormData({ ...roomFormData, name: e.target.value })}
                placeholder="e.g., Team Strategy"
                className="border-neon-green/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="room-description">Description (Optional)</Label>
              <Textarea
                id="room-description"
                value={roomFormData.description}
                onChange={(e) => setRoomFormData({ ...roomFormData, description: e.target.value })}
                placeholder="Room description..."
                className="border-neon-green/30"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-public"
                checked={roomFormData.isPublic}
                onCheckedChange={(checked) => setRoomFormData({ ...roomFormData, isPublic: checked })}
              />
              <Label htmlFor="is-public" className="cursor-pointer">
                Public room (visible to all users)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateRoomDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateRoom}
              className="bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
              disabled={!roomFormData.name.trim()}
            >
              Create Room
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mute User Dialog */}
      <Dialog open={muteDialogOpen} onOpenChange={setMuteDialogOpen}>
        <DialogContent className="border-neon-green/30">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Ban className="h-5 w-5 text-orange-500" />
              <span>Mute User</span>
            </DialogTitle>
            <DialogDescription>
              Mute {muteFormData.username} from sending messages
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="room-specific"
                checked={muteFormData.roomSpecific}
                onCheckedChange={(checked) =>
                  setMuteFormData({ ...muteFormData, roomSpecific: checked })
                }
              />
              <Label htmlFor="room-specific" className="cursor-pointer">
                Only mute in this room ({activeRoom?.name})
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-permanent"
                checked={muteFormData.isPermanent}
                onCheckedChange={(checked) =>
                  setMuteFormData({ ...muteFormData, isPermanent: checked })
                }
              />
              <Label htmlFor="is-permanent" className="cursor-pointer text-red-500">
                Permanent mute
              </Label>
            </div>

            {!muteFormData.isPermanent && (
              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Select
                  value={muteFormData.durationMinutes.toString()}
                  onValueChange={(value) =>
                    setMuteFormData({ ...muteFormData, durationMinutes: parseInt(value) })
                  }
                >
                  <SelectTrigger className="border-neon-green/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                    <SelectItem value="360">6 hours</SelectItem>
                    <SelectItem value="720">12 hours</SelectItem>
                    <SelectItem value="1440">24 hours</SelectItem>
                    <SelectItem value="10080">7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="mute-reason">Reason (Optional)</Label>
              <Textarea
                id="mute-reason"
                value={muteFormData.reason}
                onChange={(e) => setMuteFormData({ ...muteFormData, reason: e.target.value })}
                placeholder="Reason for muting this user..."
                className="border-neon-green/30"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMuteDialogOpen(false)
                setMuteFormData({
                  userId: null,
                  username: '',
                  durationMinutes: 30,
                  reason: '',
                  isPermanent: false,
                  roomSpecific: true,
                })
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMuteUser}
              className="bg-orange-500/10 hover:bg-orange-500/20 text-orange-500 border border-orange-500/30"
            >
              <Ban className="h-4 w-4 mr-2" />
              Mute User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
